import React, { useEffect, useState, useCallback } from "react";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import { api } from "@/lib/api";

/**
 * Notification history — the "inbox" that catches every push the user was
 * sent, whether or not their device was online to receive it live. Uses the
 * `notifications` collection persisted by push_notify.send_push.
 */

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.max(0, Math.floor((now - then) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch {
      // toast already handled by api interceptor if any
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const markAllRead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/notifications/mark-read");
      toast.success(`${data.updated} notification${data.updated === 1 ? "" : "s"} marked read`);
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const unreadCount = items.filter((n) => !n.read_at).length;

  return (
    <div data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle={`${items.length} in your inbox · ${unreadCount} unread`}
        actions={
          unreadCount > 0 && (
            <button
              onClick={markAllRead}
              disabled={busy}
              data-testid="mark-all-read-button"
              className="bg-gray-900 hover:bg-gray-800 disabled:opacity-50 text-white font-semibold uppercase tracking-widest text-xs px-4 py-2 transition-colors flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />}
              Mark all read
            </button>
          )
        }
      />
      <div className="px-8 pb-16 max-w-3xl">
        {loading && (
          <div className="py-12 flex items-center justify-center text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="py-16 text-center border border-dashed border-gray-300">
            <Bell className="w-8 h-8 text-gray-400 mx-auto mb-3" strokeWidth={1.5} />
            <div className="text-sm text-gray-600">No notifications yet.</div>
            <div className="text-xs text-gray-400 mt-1">
              You'll see load assignments, invoice flags, and delivery alerts here.
            </div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <ul className="border border-gray-200 divide-y divide-gray-200 bg-white">
            {items.map((n) => (
              <li
                key={n.id}
                data-testid={`notification-item-${n.id}`}
                className={`p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors ${
                  !n.read_at ? "bg-orange-50/40" : ""
                }`}
              >
                <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                  !n.read_at ? "bg-orange-500" : "bg-transparent"
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-semibold text-sm text-gray-900 truncate">
                      {n.title}
                    </div>
                    <div className="text-xs text-gray-500 flex-shrink-0">
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 mt-0.5">{n.body}</div>
                  {n.url && n.url !== "/" && (
                    <Link
                      to={n.url}
                      className="inline-block mt-1.5 text-xs uppercase tracking-widest font-semibold text-orange-600 hover:text-orange-700"
                    >
                      Open →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
